import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ProtectedRoute from './ProtectedRoute';
import LoginForm from './LoginForm';
import AdminLayout from './AdminLayout';
import Dashboard from './Dashboard';
import DepartureDateManagement from './DepartureDateManagement';

// Lazy load management components
const BookingsManagement = React.lazy(() => import('./BookingsManagement'));
const PackagesManagement = React.lazy(() => import('./PackagesManagement'));
const UsersManagement = React.lazy(() => import('./UsersManagement'));
const DealsManagement = React.lazy(() => import('./DealsManagement'));
const PromotionsManagement = React.lazy(() => import('./PromotionsManagement'));
const CategoriesManagement = React.lazy(() => import('./CategoriesManagement'));

// Loading component for suspense fallbacks
const LoadingFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="400px"
    sx={{ width: '100%' }}
  >
    <CircularProgress />
  </Box>
);

// Settings component placeholder
const Settings = () => (
  <Box sx={{ p: 3 }}>
    <h2>System Settings</h2>
    <p>Settings panel coming soon...</p>
  </Box>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public route - Login */}
      <Route
        path="/login"
        element={
          <ProtectedRoute requireAuth={false}>
            <LoginForm />
          </ProtectedRoute>
        }
      />

      {/* Protected routes - Admin Panel */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        {/* Nested routes within AdminLayout */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        <Route
          path="bookings"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <BookingsManagement />
            </Suspense>
          }
        />
        
        <Route
          path="packages"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PackagesManagement />
            </Suspense>
          }
        />
        
        <Route
          path="users"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <UsersManagement />
            </Suspense>
          }
        />
        
        <Route
          path="deals"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <DealsManagement />
            </Suspense>
          }
        />
        
        <Route
          path="departure-date"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <DepartureDateManagement />
            </Suspense>
          }
        />
        
        <Route
          path="promotions"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PromotionsManagement />
            </Suspense>
          }
        />
        
        <Route
          path="categories"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <CategoriesManagement />
            </Suspense>
          }
        />
        
        <Route path="settings" element={<Settings />} />
        
        {/* Catch all route - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default AppRoutes;